import { FC, useContext, useEffect, useState } from 'react';
import { i18n } from '@lingui/core';
import { Text } from '@mantine/core';
import { MailFolderDto, MailOutBoundConfigDTO } from '../../../Types/MailT';
import NavigationLink from '../../../Atoms/Navigation/NavigationLink';
import EmailBoxCounter from '../EmailBoxCounter';
import { EmailBoxContext } from '../../../Service/Context/EmailBoxContext';

interface Props {
  config: MailOutBoundConfigDTO;
  folder: MailFolderDto;
  closeNavBar: () => void;
}

const DefaultFolder: FC<Props> = ({ folder, config, closeNavBar }) => {
  const [folderTree, setFolderTree] = useState(folder);
  const { openedFolder } = useContext(EmailBoxContext);
  const { children } = folderTree;
  const openedFolderConfigId = openedFolder?.configId;
  const openedFolderFullName = openedFolder?.fullName;
  const openedFolderSrc = openedFolder?.src;
  const openedFolderUnreadMessages = openedFolder?.unreadMessages;

  useEffect(() => {
    setFolderTree(folder);
  }, [folder]);

  useEffect(() => {
    if (openedFolderSrc === 'IMAP' && openedFolderConfigId === config.id && openedFolderFullName) {
      setFolderTree((prev) => updateFolderUnreadMessages(prev, openedFolderFullName, openedFolderUnreadMessages ?? 0));
    }
  }, [config.id, openedFolderConfigId, openedFolderFullName, openedFolderSrc, openedFolderUnreadMessages]);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {children?.map((f) => (
        <Folder key={f.fullName} config={config} folder={f} closeNavBar={closeNavBar} />
      ))}
    </>
  );
};

const updateFolderUnreadMessages = (
  folder: MailFolderDto,
  fullName: string,
  unreadMessages: number,
): MailFolderDto => ({
  ...folder,
  unreadMessages: folder.fullName === fullName ? unreadMessages : folder.unreadMessages,
  children: folder.children?.map((child) => updateFolderUnreadMessages(child, fullName, unreadMessages)),
});

const Folder: FC<Props> = ({ folder, config, closeNavBar }) => {
  const { openedFolder, openFolder } = useContext(EmailBoxContext);
  const { id: configId } = config;
  const { fullName: folderName, configId: folderConfigId } = openedFolder ?? {};
  const { name, fullName, children, unreadMessages, hasChildren } = folder;

  return (
    <NavigationLink
      label={
        <Text my={1} sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {i18n._(`email.folder.${name}`, undefined, { message: name })}
        </Text>
      }
      defaultOpened={hasChildren}
      active={folderName === fullName && configId === folderConfigId}
      onClick={() => {
        if (!hasChildren) {
          openFolder({ ...folder, src: 'IMAP', configId });
          closeNavBar();
        }
      }}
      variant="filled"
      rightSection={
        // eslint-disable-next-line no-nested-ternary
        hasChildren ? null : <EmailBoxCounter count={unreadMessages} />
      }
    >
      {hasChildren
        ? children?.map((f) => <Folder key={f.fullName} folder={f} config={config} closeNavBar={closeNavBar} />)
        : null}
    </NavigationLink>
  );
};

export default DefaultFolder;
